import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {IPresentationMeta} from '../../../incase-domain/presentation';
import Modal from '../../../incase-domain/components/modals/directory/Modal';
import {INITIAL_WIZARD_STATE, WIZARD_CONFIG, WizardContext, type WizardHeaderTranslationKey} from './steps';
import BaseWizardStartStep from './steps/BaseWizardStartStep';
import OnDemandStep from './steps/OnDemandStep';
import LiveEventStep from './steps/live/LiveEventStep';
import RoomSelectionStep from './steps/live/RoomSelectionStep';
import RoomCreationStep from './steps/live/RoomCreationStep';
import AccessLinksStep from './steps/AccessLinksStep';
import WizardFooter from './WizardFooter';

interface IPresentationWizard {
    presentation: IPresentationMeta;
    open: boolean;
    close: () => void;
    onPresentationUpdate?: (updatedPresentation: IPresentationMeta) => void;
}

function PresentationWizard({open, close, presentation, onPresentationUpdate}: IPresentationWizard): JSX.Element {
    const {t} = useTranslation();

    const [wizardContext, setWizardContext] = useState<WizardContext>(INITIAL_WIZARD_STATE);
    const [selectedRoomId, setSelectedRoomId] = useState<string | undefined>(undefined);
    const [selectedPresenterLinkCode, setSelectedPresenterLinkCode] = useState<string | undefined>(undefined);
    const [createRoomModalOpen, setCreateRoomModalOpen] = useState(false);
    const [selectedRunOption, setSelectedRunOption] = useState<string | null>(WIZARD_CONFIG.RUN_CASE.options.PREVIEW);

    const handleClose = useCallback(() => {
        setWizardContext(INITIAL_WIZARD_STATE);
        setSelectedRoomId(undefined);
        setSelectedPresenterLinkCode(undefined);
        setCreateRoomModalOpen(false);
        setSelectedRunOption(null);
        close();
    }, [close]);

    const handleRunOptionSelect = useCallback((option: string) => {
        setSelectedRunOption(option);
    }, []);

    const renderCurrentStep = useCallback(() => {
        const stepProps = {
            wizardContext,
            setWizardContext,
            presentation,
        };

        switch (wizardContext.currentStep.key) {
            case WIZARD_CONFIG.ON_DEMAND.key:
                return <OnDemandStep {...stepProps} onPresentationUpdate={onPresentationUpdate} />;
            case WIZARD_CONFIG.LIVE_EVENT.key:
                return <LiveEventStep {...stepProps} setCreateRoomModalOpen={setCreateRoomModalOpen} />;
            case WIZARD_CONFIG.LIVE_EVENT.options.ROOM_SELECTION.key:
                return (
                    <RoomSelectionStep
                        {...stepProps}
                        selectedRoomId={selectedRoomId}
                        setSelectedRoomId={setSelectedRoomId}
                        setSelectedPresenterLinkCode={setSelectedPresenterLinkCode}
                    />
                );
            case WIZARD_CONFIG.LIVE_EVENT.options.ROOM_CREATION.key:
                return (
                    <RoomCreationStep
                        {...stepProps}
                        openRoomCreationModal={createRoomModalOpen}
                        closeRoomCreationModal={() => setCreateRoomModalOpen(false)}
                        setCreatedRoomId={setSelectedRoomId}
                        setCreatedPresenterLinkCode={setSelectedPresenterLinkCode}
                    />
                );
            case WIZARD_CONFIG.ON_DEMAND.options.ACCESS_LINKS.key:
                return <AccessLinksStep {...stepProps} roomId={selectedRoomId} presenterLinkCode={selectedPresenterLinkCode} />;
            case WIZARD_CONFIG.PREVIEW.key:
                return <BaseWizardStartStep {...stepProps} selectedOption={selectedRunOption} onOptionSelect={handleRunOptionSelect} />;
            case WIZARD_CONFIG.RUN_CASE.key:
            default:
                return <BaseWizardStartStep {...stepProps} selectedOption={selectedRunOption} onOptionSelect={handleRunOptionSelect} />;
        }
    }, [
        wizardContext,
        presentation,
        selectedPresenterLinkCode,
        onPresentationUpdate,
        selectedRoomId,
        createRoomModalOpen,
        selectedRunOption,
        handleRunOptionSelect,
    ]);

    const disableNext = wizardContext.currentStep.key === WIZARD_CONFIG.LIVE_EVENT.options.ROOM_SELECTION.key && !selectedRoomId;

    return (
        <Modal isOpen={open} onCancel={handleClose} showCancel={false} showConfirm={false} closeOnClickOutside size="md">
            <div className="c-case_wizard">
                <div className="c-case_wizard__title">{t(wizardContext.currentStep.header as WizardHeaderTranslationKey)}</div>
                <div className="c-case_wizard__content">{renderCurrentStep()}</div>
            </div>
            <WizardFooter
                wizardContext={wizardContext}
                setWizardContext={setWizardContext}
                presentation={presentation}
                disabledNext={disableNext}
                selectedRunOption={selectedRunOption}
                close={close}
            />
        </Modal>
    );
}

export default PresentationWizard;
